import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { getStoredUser } from "../lib/auth";
import { useLanguage } from "../lib/language";

function AdminStudentsPage() {
  const user = getStoredUser();
  const { language } = useLanguage();
  const isArabic = language === "ar";

  const copy = isArabic
    ? {
        eyebrow: "إدارة الطلبة",
        title: "إدارة الطلبة",
        subtitle: "راجع بيانات كل طالب، الكورسات المشترك فيها، وعدد مرات التسليم وتقارير الأداء الأخيرة.",
        search: "ابحث عن طالب",
        searchPlaceholder: "الاسم أو البريد أو المحافظة",
        loading: "جار تحميل بيانات الطلبة...",
        empty: "لا يوجد طلبة حاليًا.",
        selectHint: "اختر طالبًا من القائمة لعرض التفاصيل.",
        courses: "الكورسات",
        submissions: "التسليمات",
        average: "متوسط الدرجات",
        studentInfo: "بيانات الطالب",
        progress: "تقدم الطالب داخل الكورسات",
        recent: "أحدث التسليمات",
        phone: "الهاتف",
        governorate: "المحافظة",
        education: "المرحلة",
        institution: "الجهة",
        noProgress: "لا يوجد تقدم مسجل لهذا الطالب بعد.",
        noRecent: "لا توجد تسليمات حديثة لهذا الطالب.",
        course: "الكورس",
        pre: "قبلي",
        post: "بعدي",
        improvement: "التحسن",
        assessment: "التقييم",
        report: "التقرير",
        score: "الدرجة"
      }
    : {
        eyebrow: "Student Management",
        title: "Student Management",
        subtitle: "Review student profiles, enrolled courses, submission counts, and recent performance reports.",
        search: "Search students",
        searchPlaceholder: "Name, email, or governorate",
        loading: "Loading students...",
        empty: "No students found yet.",
        selectHint: "Select a student from the list to view details.",
        courses: "Courses",
        submissions: "Submissions",
        average: "Average score",
        studentInfo: "Student info",
        progress: "Student course progress",
        recent: "Recent submissions",
        phone: "Phone",
        governorate: "Governorate",
        education: "Stage",
        institution: "Institution",
        noProgress: "No progress has been recorded for this student yet.",
        noRecent: "There are no recent submissions for this student.",
        course: "Course",
        pre: "Pre",
        post: "Post",
        improvement: "Improvement",
        assessment: "Assessment",
        report: "Report",
        score: "Score"
      };

  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [search, setSearch] = useState("");
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function fetchStudents() {
      if (!user?.id) {
        return;
      }
      try {
        setIsLoadingList(true);
        const response = await api.get("/admin/students", { params: { admin_id: user.id } });
        if (!active) {
          return;
        }
        const nextStudents = response.data?.students || [];
        setStudents(nextStudents);
        setSelectedStudentId((current) => current || nextStudents[0]?.id || null);
      } catch (requestError) {
        if (active) {
          setError(requestError.response?.data?.error || copy.loading);
        }
      } finally {
        if (active) {
          setIsLoadingList(false);
        }
      }
    }

    fetchStudents();
    return () => {
      active = false;
    };
  }, [copy.loading, user?.id]);

  useEffect(() => {
    let active = true;

    async function fetchStudentDetails() {
      if (!user?.id || !selectedStudentId) {
        setSelectedStudent(null);
        return;
      }
      try {
        setIsLoadingDetail(true);
        const response = await api.get(`/admin/students/${selectedStudentId}`, {
          params: { admin_id: user.id }
        });
        if (active) {
          setSelectedStudent(response.data);
        }
      } catch (requestError) {
        if (active) {
          setError(requestError.response?.data?.error || copy.loading);
        }
      } finally {
        if (active) {
          setIsLoadingDetail(false);
        }
      }
    }

    fetchStudentDetails();
    return () => {
      active = false;
    };
  }, [copy.loading, selectedStudentId, user?.id]);

  const filteredStudents = useMemo(() => {
    const term = search.trim().toLowerCase();
    return students.filter((student) => {
      if (!term) {
        return true;
      }
      return [student.full_name, student.email, student.governorate]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [search, students]);

  const summary = useMemo(() => {
    return filteredStudents.reduce(
      (accumulator, student) => ({
        students: accumulator.students + 1,
        courses: accumulator.courses + Number(student.courses_count || 0),
        submissions: accumulator.submissions + Number(student.submissions_count || 0)
      }),
      { students: 0, courses: 0, submissions: 0 }
    );
  }, [filteredStudents]);

  return (
    <section className="workspace-page admin-page">
      <div className="hero-panel">
        <div>
          <p className="section-eyebrow">{copy.eyebrow}</p>
          <h2 className="section-title">{copy.title}</h2>
          <p className="section-subtitle">{copy.subtitle}</p>
        </div>
      </div>

      <section className="metric-grid admin-summary-grid admin-summary-grid--compact">
        <article className="metric-card admin-stat-card">
          <span>{isArabic ? "عدد الطلبة" : "Students"}</span>
          <strong>{summary.students}</strong>
        </article>
        <article className="metric-card admin-stat-card">
          <span>{copy.courses}</span>
          <strong>{summary.courses}</strong>
        </article>
        <article className="metric-card admin-stat-card">
          <span>{copy.submissions}</span>
          <strong>{summary.submissions}</strong>
        </article>
      </section>

      <div className="admin-management-layout">
        <div className="panel-card admin-management-list">
          <div className="field-stack">
            <label className="field-label">{copy.search}</label>
            <input
              className="text-input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={copy.searchPlaceholder}
            />
          </div>

          {isLoadingList ? (
            <div className="state-panel state-panel--loading"><p>{copy.loading}</p></div>
          ) : !filteredStudents.length ? (
            <div className="state-panel state-panel--empty"><p>{copy.empty}</p></div>
          ) : (
            <div className="admin-record-stack">
              {filteredStudents.map((student) => (
                <button
                  key={student.id}
                  type="button"
                  className={selectedStudentId === student.id ? "admin-record-card admin-record-card--active" : "admin-record-card"}
                  onClick={() => setSelectedStudentId(student.id)}
                >
                  <div className="admin-record-card__header">
                    <strong>{student.full_name}</strong>
                    <span className="status-pill">{student.courses_count || 0} {copy.courses}</span>
                  </div>
                  <p className="helper-text">{student.email}</p>
                  <div className="admin-record-card__meta">
                    <span>{copy.submissions}: {student.submissions_count || 0}</span>
                    <span>{copy.average}: {student.average_score ?? "-"}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="panel-card admin-management-detail">
          {!selectedStudentId ? (
            <div className="state-panel state-panel--empty"><p>{copy.selectHint}</p></div>
          ) : isLoadingDetail || !selectedStudent ? (
            <div className="state-panel state-panel--loading"><p>{copy.loading}</p></div>
          ) : (
            <div className="stack-lg">
              <div className="card-title-row">
                <div>
                  <h3>{selectedStudent.student.full_name}</h3>
                  <p className="helper-text">{selectedStudent.student.email}</p>
                </div>
                <span className="status-pill">{selectedStudent.summary.courses_count || 0} {copy.courses}</span>
              </div>

              <article className="admin-info-card">
                <h4>{copy.studentInfo}</h4>
                <div className="two-column-grid">
                  <span>{copy.phone}: {selectedStudent.student.phone_number || "-"}</span>
                  <span>{copy.governorate}: {selectedStudent.student.governorate || "-"}</span>
                  <span>{copy.education}: {selectedStudent.student.education_level || "-"}</span>
                  <span>{copy.institution}: {selectedStudent.student.institution_name || "-"}</span>
                </div>
              </article>

              <div>
                <h4>{copy.progress}</h4>
                {!selectedStudent.course_progress.length ? (
                  <div className="state-panel state-panel--empty"><p>{copy.noProgress}</p></div>
                ) : (
                  <div className="table-shell">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{copy.course}</th>
                          <th>{copy.pre}</th>
                          <th>{copy.post}</th>
                          <th>{copy.improvement}</th>
                          <th>{copy.submissions}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedStudent.course_progress.map((course) => (
                          <tr key={course.course_id}>
                            <td>{course.course_title}</td>
                            <td>{course.pre_score ?? "-"}</td>
                            <td>{course.post_score ?? "-"}</td>
                            <td>{course.improvement ?? "-"}</td>
                            <td>{course.submissions_count || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h4>{copy.recent}</h4>
                {!selectedStudent.recent_submissions.length ? (
                  <div className="state-panel state-panel--empty"><p>{copy.noRecent}</p></div>
                ) : (
                  <div className="table-shell">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{copy.course}</th>
                          <th>{copy.assessment}</th>
                          <th>{copy.score}</th>
                          <th>{copy.report}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedStudent.recent_submissions.map((submission) => (
                          <tr key={submission.submission_id}>
                            <td>{submission.course_title}</td>
                            <td>{submission.assessment_title}</td>
                            <td>{submission.score ?? "-"}</td>
                            <td>{submission.feedback_summary || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
          {error && <div className="feedback-banner feedback-banner--error">{error}</div>}
        </div>
      </div>
    </section>
  );
}

export default AdminStudentsPage;
